// TEST-ONLY: silent Firebase ID-token renewal.
//
// Loaded by src/axios.js only when VUE_APP_SILENT_TOKEN_RENEWAL === 'on', which is set in
// .env.test and nowhere else. The LIVE build must never contain this file:
// scripts/check-bundle-endpoint.js fails a "live" build if MARKER appears in dist/js.
//
// Why: the ID token lives one hour and was only renewed in the response-success
// interceptor, so after an hour idle the first request went out expired, got 401/403 and
// the re-login dialog opened. Here the token is renewed BEFORE the request when it is due,
// and a 401/403 gets one shared refresh and one retry before the re-login dialog.

import { isRefusal } from './httpRefusal'

export const MARKER = 'mbp-silent-token-renewal'

// Renew when the ID token has less than this left (i.e. it is older than ~50 minutes).
const EXPIRY_MARGIN_MS = 10 * 60 * 1000

const parseJwtExpiry = token => {
  try {
    const part = String(token).split('.')[1]
    if (!part) return null
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const json = JSON.parse(atob(b64 + '==='.slice((b64.length + 3) % 4)))
    return json && json.exp ? json.exp * 1000 : null
  } catch (e) {
    return null
  }
}

// Same exclusions as the Authorization header in src/axios.js: the Google token/sign-in
// endpoints (which carry their own key) and the sign-up completion call.
const isSkippedUrl = url =>
  !url ||
  url.indexOf('https://securetoken.googleapis.com/') === 0 ||
  url.indexOf('https://identitytoolkit.googleapis.com/') === 0 ||
  url === '/v1/user/completeSignup'

// After a failed refresh, do not try again (before a request or on a 401) for this long.
const FAILED_REFRESH_BACKOFF_MS = 5 * 60 * 1000

// `getStore` (not the store itself): src/axios.js is first evaluated while store/store.js is
// still evaluating (store -> salesForecastActions -> SalesService -> axios -> store), so at
// that moment its `store` import is still undefined. Read it only when a request runs.
export default function createSilentTokenRenewal ({ store, getStore = () => store, refreshToken, now = () => new Date().getTime() }) {
  let inFlight = null
  let backoffUntil = 0
  const inBackoff = () => now() < backoffUntil

  const hasUsableToken = t => t && t !== 'null'

  const isDue = () => {
    if (inBackoff()) return false
    const store = getStore()
    const refresh = store.getters['auth/getMyRefreshToken']
    if (!hasUsableToken(refresh)) return false
    const t = now()
    const next = store.getters['auth/getNextRefreshTime']
    if (next && t > next) return true
    const exp = parseJwtExpiry(store.getters['auth/getMyToken'])
    return !!(exp && t > exp - EXPIRY_MARGIN_MS)
  }

  // One refresh at a time; every caller waiting on it gets the same promise.
  const refreshShared = () => {
    if (!inFlight) {
      const store = getStore()
      const refresh = store.getters['auth/getMyRefreshToken']
      inFlight = Promise.resolve()
        .then(() => {
          if (!hasUsableToken(refresh)) throw new Error(MARKER + ': no refresh token')
          return refreshToken(refresh)
        })
        .then(data => {
          // Same store updates as the response interceptor / LoginForm.vue; the auth
          // actions also persist the tokens to localStorage.
          store.dispatch('auth/setMyToken', data.id_token)
          store.dispatch('auth/setMyRefreshToken', data.refresh_token)
          store.dispatch('auth/setNextRefreshTime', now() + 300000)
          backoffUntil = 0
          return data.id_token
        }, err => {
          // Back off so a dead refresh token is not re-tried on every request.
          backoffUntil = now() + FAILED_REFRESH_BACKOFF_MS
          store.dispatch('auth/setNextRefreshTime', backoffUntil)
          throw err
        })
      const clear = () => { inFlight = null }
      inFlight.then(clear, clear)
    }
    return inFlight
  }

  // Request side: renew first if due. A failed refresh - or any fault in here - lets the
  // request go out with the current token; the 401 path below then decides. Never rejects.
  const beforeRequest = config => {
    try {
      if (!config || isSkippedUrl(config.url) || !isDue()) return Promise.resolve(config)
      return refreshShared().then(() => config, () => config)
    } catch (e) {
      return Promise.resolve(config)
    }
  }

  // Error side: returns a promise of the retried response, or null when this error is not
  // ours to handle. If the refresh itself fails, `fallback(error)` - the existing error
  // handler (clear tokens, re-login dialog) - runs instead.
  const retryAfterRefresh = (error, axiosInstance, fallback) => {
    const config = error && error.config
    const status = error && error.response && error.response.status
    if (!config || (status !== 401 && status !== 403)) return null
    if (isRefusal(error.response)) return null
    if (config._retriedAfterRefresh || isSkippedUrl(config.url) || inBackoff()) return null
    if (!hasUsableToken(getStore().getters['auth/getMyRefreshToken'])) return null
    config._retriedAfterRefresh = true
    return refreshShared().then(
      () => axiosInstance(config),
      () => fallback(error)
    )
  }

  return { MARKER, isDue, refreshShared, beforeRequest, retryAfterRefresh }
}
